/**
 * URL'in geçerliliğini kontrol eder ve gerekirse düzeltir
 * @param {string} url Kontrol edilecek URL
 * @returns {string|null} Düzeltilmiş veya doğrulanmış URL, geçersizse null
 */
export const validateAndFixUrl = (url) => {
  if (!url) return null

  try {
    new URL(url)
    return url // URL geçerliyse olduğu gibi döndür
  } catch (e) {
    // URL geçersiz, düzeltmeyi dene
    if (!url.startsWith('http')) {
      const fixedUrl = 'https://' + url
      try {
        new URL(fixedUrl)
        return fixedUrl // Düzeltilmiş URL geçerli
      } catch {
        return null // Düzeltme sonrası hala geçersiz
      }
    }
    return null // HTTP ile başlıyor ama yine de geçersiz
  }
}

/**
 * URL'nin bileşenlerini ayırır
 * @param {string} url Ayrıştırılacak URL
 * @returns {object|null} URL bileşenleri veya geçersizse null
 */
export const parseUrl = (url) => {
  const fixedUrl = validateAndFixUrl(url)
  if (!fixedUrl) return null

  try {
    const urlObj = new URL(fixedUrl)
    return {
      protocol: urlObj.protocol, // örn: "https:"
      hostname: urlObj.hostname, // örn: "example.com"
      pathname: urlObj.pathname, // örn: "/path/page"
      search: urlObj.search,     // örn: "?query=test"
      hash: urlObj.hash,         // örn: "#section"
      port: urlObj.port,         // örn: "8080"
      origin: urlObj.origin,     // örn: "https://example.com"
      username: urlObj.username,
      password: urlObj.password,
    }
  } catch (e) {
    return null
  }
}

/**
 * URL'den belirli bir parametrenin değerini döndürür
 * @param {string} url URL
 * @param {string} paramName Parametre adı
 * @returns {string|null} Parametre değeri veya bulunamazsa null
 */
export const getUrlParameter = (url, paramName) => {
  const fixedUrl = validateAndFixUrl(url)
  if (!fixedUrl) return null

  try {
    const urlObj = new URL(fixedUrl)
    return urlObj.searchParams.get(paramName)
  } catch (e) {
    return null
  }
}

/**
 * URL'ye yeni parametreler ekler veya mevcut parametreleri günceller
 * @param {string} url Temel URL
 * @param {object} params Eklenecek parametreler
 * @returns {string|null} Güncellenmiş URL veya geçersizse null
 */
export const addUrlParameters = (url, params) => {
  const fixedUrl = validateAndFixUrl(url)
  if (!fixedUrl || !params) return fixedUrl

  try {
    const urlObj = new URL(fixedUrl)

    Object.entries(params).forEach(([key, value]) => {
      urlObj.searchParams.set(key, value)
    })

    return urlObj.toString()
  } catch (e) {
    return fixedUrl
  }
}

/**
 * URL'den belirtilen parametreleri kaldırır
 * @param {string} url URL
 * @param {string[]} paramNames Kaldırılacak parametre adları
 * @returns {string|null} Güncellenmiş URL veya geçersizse null
 */
export const removeUrlParameters = (url, paramNames) => {
  const fixedUrl = validateAndFixUrl(url)
  if (!fixedUrl || !paramNames?.length) return fixedUrl

  try {
    const urlObj = new URL(fixedUrl)

    paramNames.forEach(param => {
      urlObj.searchParams.delete(param)
    })

    return urlObj.toString()
  } catch (e) {
    return fixedUrl
  }
}

/**
 * URL'yi normalleştirir (gereksiz bileşenleri temizler)
 * @param {string} url Normalleştirilecek URL
 * @returns {string|null} Normalleştirilmiş URL veya geçersizse null
 */
export const normalizeUrl = (url) => {
  const fixedUrl = validateAndFixUrl(url)
  if (!fixedUrl) return null

  try {
    let urlObj = new URL(fixedUrl)

    // UTM parametrelerini temizle
    const utmParams = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content']
    utmParams.forEach(param => urlObj.searchParams.delete(param))

    // Gereksiz fragment'ları temizle
    urlObj.hash = ''

    // Sondaki slash'ı normalleştir
    let path = urlObj.pathname
    if (path !== '/' && path.endsWith('/')) {
      path = path.slice(0, -1)
      urlObj = new URL(path, urlObj.origin)
    }

    return urlObj.toString()
  } catch (e) {
    return fixedUrl
  }
}

/**
 * Göreceli URL'yi mutlak URL'ye dönüştürür
 * @param {string} relativeUrl Göreceli URL
 * @param {string} baseUrl Temel URL
 * @returns {string|null} Mutlak URL veya geçersizse null
 */
export const resolveRelativeUrl = (relativeUrl, baseUrl) => {
  if (!relativeUrl) return null

  const fixedBaseUrl = validateAndFixUrl(baseUrl)
  if (!fixedBaseUrl) return null

  try {
    return new URL(relativeUrl, fixedBaseUrl).toString()
  } catch (e) {
    return null
  }
}

/**
 * URL'nin bir dosya URL'si olup olmadığını kontrol eder
 * @param {string} url Kontrol edilecek URL
 * @returns {boolean} Dosya URL'si ise true
 */
export const isFileUrl = (url) => {
  const fixedUrl = validateAndFixUrl(url)
  if (!fixedUrl) return false

  try {
    const urlObj = new URL(fixedUrl)
    return urlObj.protocol === 'file:'
  } catch (e) {
    return false
  }
}

/**
 * URL'den bir dosya adı çıkarır
 * @param {string} url URL
 * @returns {string|null} Dosya adı veya bulunamazsa null
 */
export const getFileNameFromUrl = (url) => {
  const fixedUrl = validateAndFixUrl(url)
  if (!fixedUrl) return null

  try {
    const urlObj = new URL(fixedUrl)
    const pathname = urlObj.pathname
    const segments = pathname.split('/')
    const lastSegment = segments[segments.length - 1]

    // URL'nin son segmenti boşsa, bir klasör
    if (!lastSegment) {
      return null
    }

    return lastSegment
  } catch (e) {
    return null
  }
}

/**
 * URL'nin bir medya URL'si (resim, video, vb.) olup olmadığını kontrol eder
 * @param {string} url Kontrol edilecek URL
 * @returns {boolean} Medya URL'si ise true
 */
export const isMediaUrl = (url) => {
  const fixedUrl = validateAndFixUrl(url)
  if (!fixedUrl) return false

  const mediaExtensions = [
    // Resimler
    '.jpg', '.jpeg', '.png', '.gif', '.svg', '.webp', '.bmp', '.ico',
    // Videolar
    '.mp4', '.webm', '.ogg', '.mov', '.avi', '.wmv', '.flv', '.mkv',
    // Ses
    '.mp3', '.wav', '.ogg', '.aac', '.flac',
  ]

  try {
    const fileName = getFileNameFromUrl(fixedUrl)
    if (!fileName) return false

    return mediaExtensions.some(ext => fileName.toLowerCase().endsWith(ext))
  } catch (e) {
    return false
  }
}

/**
 * URL'den alan adını (domain) çıkarır
 * @param {string} url URL
 * @returns {string|null} Alan adı veya geçersizse null
 */
export const getDomainFromUrl = (url) => {
  const fixedUrl = validateAndFixUrl(url)
  if (!fixedUrl) return null

  try {
    const urlObj = new URL(fixedUrl)
    return urlObj.hostname
  } catch (e) {
    return null
  }
}

/**
 * URL'den ana alan adını (apex domain) çıkarır
 * @param {string} url URL
 * @returns {string|null} Ana alan adı veya geçersizse null
 */
export const getApexDomainFromUrl = (url) => {
  const domain = getDomainFromUrl(url)
  if (!domain) return null

  // IP adreslerini kontrol et
  if (/^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(domain)) {
    return domain
  }

  const parts = domain.split('.')
  if (parts.length <= 2) return domain

  // Şu gibi özel TLD'leri kontrol et: co.uk, com.tr vb.
  const specialTLDs = ['co.uk', 'co.jp', 'com.tr', 'com.br', 'org.uk']
  const domainWithLastTwoParts = parts.slice(-2).join('.')

  if (specialTLDs.includes(domainWithLastTwoParts)) {
    // Özel TLD durumunda son 3 parçayı al
    return parts.slice(-3).join('.')
  }

  // Normal durumda son 2 parçayı al
  return parts.slice(-2).join('.')
}